// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import {
  GetStaticComponentProps,
  Text,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

// Components
import Component from 'src/helpers/Component/Component';
import { ProjectCountAndReviewAggregateTheme } from './ProjectCountAndReviewAggregate.theme';
import { StarRating } from 'src/helpers/StarRating';

// Lib
import {
  getReviewsSummary,
  parseReviewFilterData,
  createReviewSummaryFilter,
  ReviewFilters,
} from 'lib/reviews/get-reviews-v2';

// Types
import type { FilterRule, ReviewSummary } from 'lib/reviews/types';
import { conditionParametersFactory } from 'lib/rules/parameters/condition-parameters';

export type ProjectCountAndReviewAggregateProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Reviews.ProjectCountAndReviewAggregate & {
    productReview?: ReviewSummary | null;
    reviewsFilterRules?: string;
  };

type ProjectCountAndReviewAggregateDatasource = ProjectCountAndReviewAggregateProps & ReviewFilters;

const ProjectCountAndReviewAggregate = (props: ProjectCountAndReviewAggregateProps) => {
  const { themeData } = useTheme(ProjectCountAndReviewAggregateTheme);

  const averageRating = props.productReview?.averageRating ?? 0;
  const totalReviews = props.productReview?.totalReviews ?? 0;
  const totalProjects = props.productReview?.totalProjects ?? 0;

  return (
    <Component variant="lg" dataComponent="reviews/projectcountandreviewaggregate" {...props}>
      <div className={themeData.classes.container}>
        {props.fields?.title?.value && (
          <div className={themeData.classes.headline}>
            <h2>{props.fields.title.value}</h2>
          </div>
        )}
        <div className={themeData.classes.ratingsWrapper}>
          {totalProjects > 0 && (
            <div className={themeData.classes.totalProjectWrapper}>
              <Text
                tag="h5"
                className={themeData.classes.totalProjectCount}
                field={{ value: totalProjects.toLocaleString() }}
              />
              {props.fields?.projectCountLabel?.value && (
                <h5 className={themeData.classes.label}>{props.fields.projectCountLabel.value}</h5>
              )}
            </div>
          )}

          <div className={themeData.classes.ratingsTextWrapper}>
            {averageRating > 0 && (
              <>
                <div className={themeData.classes.ratingsIconsList}>
                  <StarRating reviewStars={averageRating} iconSize="30" />
                </div>

                <div className={themeData.classes.totalReviewsText}>
                  <Text
                    tag="h5"
                    className={themeData.classes.label}
                    field={{ value: totalReviews.toLocaleString() }}
                  />
                  <h5 className={themeData.classes.ratingText}>Ratings</h5>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Component>
  );
};

export default withDatasourceCheck()<ProjectCountAndReviewAggregateProps>(
  ProjectCountAndReviewAggregate
);

/* istanbul ignore next - We aren't running E2E tests. */
export const getStaticProps: GetStaticComponentProps = async (rendering, layout, context) => {
  const datasource = rendering as ProjectCountAndReviewAggregateDatasource;

  if (!datasource) {
    return {};
  }

  const rules = await conditionParametersFactory.extend(
    datasource.fields?.reviewFilters?.value,
    context,
    layout
  );

  const filters = parseReviewFilterData({
    reviewFilterProps: datasource,
    reviewFilters: rules,
  });

  const summaryFilter = createReviewSummaryFilter(filters.filterRules as FilterRule);
  const reviewSummary = await getReviewsSummary(summaryFilter);

  return {
    productReview: reviewSummary ?? undefined,
    reviewsFilterRules: rules,
  };
};
